import { useEffect, useRef, useState, useContext } from 'react';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { Switch, Dropdown, Menu } from 'antd';
import Icon from '@ant-design/icons';

import { deletePage, editPage, getPagesList } from 'services/pages';

import { ContextWrapper } from 'contexts/wrapper.context';
import { useModal } from 'contexts/modalProvider.context';
import { getPageName, getPageFullURL, visitPage } from 'utils/pages';

import BRTable from 'components/BRNewTable/BRTable';
import ConfirmationModal from 'components/BRModals/ConfirmationModal/ConfirmationModal';
import { notify } from 'components/Notify/Notify';

import { ReactComponent as ActionsIcon } from 'assets/bosta-icons/More-Actions.svg';
import { ReactComponent as DocumentIcon } from 'assets/bosta-icons/Document.svg';

import './PagesTable.less';

const PagesTable = ({ pages, setIsLoading, setPages, permissions }) => {
  const [visiblePagesCount, setVisiblePagesCount] = useState(0);

  const { businessInfo } = useContext(ContextWrapper);
  const { formatMessage } = useIntl();
  const refreshTable = useRef();
  const history = useHistory();
  const { openModal } = useModal();

  const { HIDE_ACTIONS_MENU, DIM_VISIBILITY_TOGGLE, HIDE_DELETE_PAGE } =
    permissions;

  const updateTable = () => refreshTable.current();

  const handleVisiblePagesCount = (pages) =>
    setVisiblePagesCount(pages.filter(({ isVisible }) => isVisible).length);

  const getTableData = async ({ page, limit }) => {
    const payload = {
      limit,
      page
    };

    try {
      const { pages = [], count = 0 } = await getPagesList(payload);
      setPages(pages);
      return {
        list: pages,
        total: count
      };
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  const handleCopyURL = (url) => {
    navigator.clipboard.writeText(
      getPageFullURL({ businessInfo, pageURL: url })
    );
    notify({
      msg: formatMessage({
        id: 'pages.copied_url_success_msg'
      }),
      type: 'success'
    });
  };

  const handleDeletePage = async ({ pageTitle, pageTitleAr, id }) => {
    setIsLoading(true);

    try {
      await deletePage(id);
      notify({
        msg: formatMessage(
          {
            id: 'pages.delete_page.confirmation_modal.success_msg'
          },
          {
            pageName: getPageName({ pageTitle, pageTitleAr })
          }
        ),
        type: 'success'
      });
      if (pages.length > 1) {
        // Not last item
        updateTable();
      } else {
        setPages([]);
      }
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const confirmOnUserBeforeDeletePage = (pageData) => {
    openModal(ConfirmationModal, {
      title: formatMessage(
        {
          id: 'pages.delete_page.confirmation_modal.title'
        },
        {
          pageName: getPageName(pageData)
        }
      ),
      content: formatMessage({
        id: 'pages.delete_page.confirmation_modal.message'
      }),
      confirmButtonLabel: formatMessage({
        id: 'pages.delete_page.confirmation_modal.confirm_btn'
      }),
      cancelButtonLabel: formatMessage({
        id: 'pages.delete_page.confirmation_modal.cancel_btn'
      }),
      modalAction: () => handleDeletePage(pageData)
    });
  };

  const confirmOnUserBeforeHidePage = (pageData) => {
    openModal(ConfirmationModal, {
      title: formatMessage(
        {
          id: 'pages.hide_page.confirmation_modal.title'
        },
        {
          pageName: getPageName(pageData)
        }
      ),
      content: formatMessage({
        id: 'pages.hide_page.confirmation_modal.message'
      }),
      confirmButtonLabel: formatMessage({
        id: 'pages.hide_page.confirmation_modal.confirm_btn'
      }),
      cancelButtonLabel: formatMessage({
        id: 'pages.hide_page.confirmation_modal.cancel_btn'
      }),
      modalAction: () =>
        handleShowHidePage({
          isVisible: false,
          messageId: 'pages.hide_page.confirmation_modal.success_msg',
          ...pageData
        })
    });
  };

  const handleShowHidePage = async ({
    pageTitle,
    pageTitleAr,
    id,
    isVisible,
    messageId,
    url
  }) => {
    setIsLoading(true);

    try {
      const payload = {
        isVisible
      };

      await editPage(id, payload);

      notify({
        msg: formatMessage(
          {
            id: messageId
          },
          {
            pageName: getPageName({ pageTitle, pageTitleAr })
          }
        ),
        type: 'success',
        ...(isVisible && {
          extraAction: {
            action: () =>
              visitPage({
                businessInfo,
                pageURL: url
              }),
            label: formatMessage({
              id: 'pages.actions.visit_page'
            })
          }
        })
      });
      updateTable();
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const handleMenuItemClick = ({ key, record }) => {
    switch (key) {
      case '0':
        handleCopyURL(record.url);
        break;

      case '1':
        history.push(`/pages/edit/${record.id}`, '_parent');
        break;

      case '2':
        confirmOnUserBeforeDeletePage(record);
        break;

      default:
        break;
    }
  };

  const actionsMenu = (record) => {
    const items = [
      {
        key: '0',
        label: formatMessage({
          id: 'pages.actions.copy_url'
        }),
        disabled: !record.isVisible
      },
      {
        key: '1',
        label: formatMessage({
          id: 'pages.actions.edit_page'
        })
      },
      ...(HIDE_DELETE_PAGE
        ? []
        : [
            {
              type: 'divider'
            },
            {
              key: '2',
              label: formatMessage({
                id: 'pages.actions.delete_page'
              }),
              danger: true
            }
          ])
    ];

    return (
      <Menu
        onClick={({ key }) => handleMenuItemClick({ key, record })}
        items={items}
      />
    );
  };

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = refreshMethod;
  };

  const columns = [
    {
      title: formatMessage({ id: 'pages.pages_list.table.columns.page_name' }),
      fixed: 'left',
      render: (pageData) => (
        <div className="br-pages-table__page-name">
          <Icon component={DocumentIcon} />
          <span>{getPageName(pageData)}</span>
        </div>
      )
    },
    {
      title: formatMessage({
        id: 'pages.pages_list.table.columns.visible_on_store'
      }),
      width: 200,
      render: ({ isVisible, ...rest }) => (
        <Switch
          disabled={DIM_VISIBILITY_TOGGLE}
          checked={isVisible}
          onChange={(checked) =>
            checked
              ? handleShowHidePage({
                  isVisible: true,
                  messageId: 'pages.show_page.success_msg',
                  ...rest
                })
              : confirmOnUserBeforeHidePage(rest)
          }
        />
      )
    },
    ...(HIDE_ACTIONS_MENU
      ? []
      : [
          {
            width: 50,
            render: (record) => (
              <Dropdown
                trigger={['click']}
                overlay={actionsMenu(record)}
                getPopupContainer={(trigger) =>
                  trigger.parentElement.parentElement.parentElement
                }
              >
                <ActionsIcon />
              </Dropdown>
            )
          }
        ])
  ];

  useEffect(() => {
    handleVisiblePagesCount(pages);
  }, [pages]);

  return (
    <BRTable
      className="br-pages-table"
      title={formatMessage(
        {
          id: `pages.pages_list.table.${
            visiblePagesCount === 1 ? 'title_singular' : 'title_plural'
          }`
        },
        {
          count: visiblePagesCount
        }
      )}
      columns={columns}
      listingData={pages}
      listFunction={getTableData}
      shareMethods={acceptMethods}
      showFilter={false}
      scroll={{
        x: 500
      }}
    />
  );
};

export default PagesTable;
